import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const APP_MODE = process.env.REACT_APP_APP_MODE;
const LAUNCH_DATE_STR = process.env.REACT_APP_LAUNCH_DATE;
const LAUNCH_DATE = LAUNCH_DATE_STR ? new Date(LAUNCH_DATE_STR).getTime() : null;

const CountdownGate = ({ children }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Only run timer if we're in live mode with a valid launch date
    if (APP_MODE !== "live" || !LAUNCH_DATE || now >= LAUNCH_DATE) return;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [now]);

  // Not live mode or no launch date → pass through immediately
  if (APP_MODE !== "live" || !LAUNCH_DATE) return children;

  // Launch date has passed → pass through
  if (now >= LAUNCH_DATE) return children;

  // Calculate remaining time
  const diff = LAUNCH_DATE - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  const timeBlocks = [
    { value: days, label: t("countdown.days", "Tage") },
    { value: hours, label: t("countdown.hours", "Stunden") },
    { value: minutes, label: t("countdown.minutes", "Minuten") },
    { value: seconds, label: t("countdown.seconds", "Sekunden") },
  ];

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 py-12">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="inline-block px-6 py-2 rounded-full bg-gradient-to-r from-[#0f7699] to-[#4c9fb9] text-white text-sm font-semibold mb-6 tracking-wide">
          EARLY ACCESS
        </div>
        <h1 className="text-3xl md:text-5xl font-bold text-slate-800 dark:text-white mb-3">
          Video-3D-Scan
        </h1>
        <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400">
          {t("countdown.comingSoon", "Demnächst verfügbar...")}
        </p>
      </div>

      {/* Countdown timer */}
      <div className="flex gap-3 sm:gap-5 mb-10">
        {timeBlocks.map(({ value, label }) => (
          <div
            key={label}
            className="flex flex-col items-center p-4 sm:p-6 rounded-2xl min-w-[70px] sm:min-w-[100px]
              bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50
              border border-slate-100 dark:border-slate-700"
          >
            <span className="text-3xl sm:text-5xl font-mono font-bold text-[#0f7699] dark:text-[#4c9fb9] tabular-nums">
              {String(value).padStart(2, "0")}
            </span>
            <span className="text-xs sm:text-sm mt-2 text-slate-400 dark:text-slate-500 uppercase tracking-wider">
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Description */}
      <p className="text-sm text-slate-400 dark:text-slate-500 text-center max-w-lg leading-relaxed">
        {t(
          "countdown.description",
          "Wir bereiten etwas Besonderes für Sie vor. Die Plattform wird bald verfügbar sein."
        )}
      </p>

      {/* Launch date hint */}
      <div className="mt-6 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 text-sm">
        {t("countdown.launchDate", "Start")}: {new Date(LAUNCH_DATE).toLocaleDateString()}
      </div>
    </div>
  );
};

export default CountdownGate;
